import { useState, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import fileDownload from 'js-file-download';
import moment from 'moment';
import ErrorHandler from '@Root/ErrorHandler';
import { Tabs, Tab, Menu, MenuItem } from '@mui/material';
import AvTimerRoundedIcon from '@mui/icons-material/AvTimerRounded';
import { ReactComponent as Download } from '@Assets/download.svg';
import { ReactComponent as DefaultFormPic } from '@Assets/form.svg';
import { ReactComponent as ViewIcon } from '@Assets/view4.svg';
import { ReactComponent as AccessIcon } from '@Assets/access.svg';
import ToggleStyleSwitch from '@CommonRepresentationalDisplay/Switch/ToggleStyleSwitch';
import TicketForm from '@IssueConsoleForms/TicketForm';
import Grid from '@mui/material/Grid';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DropDownArrow from '@mui/icons-material/KeyboardArrowDown';
import { HiPlus as CreateIcon } from 'react-icons/hi';
import Popover from '@mui/material/Popover';
import { IoClose as Close } from 'react-icons/io5';
import Skeleton from '@mui/material/Skeleton';
import CircularProgress from '@mui/material/CircularProgress';
import { useMediaQuery } from 'react-responsive';
import SettingsPage from '@IssueConsolePages/SettingsPage';
import TicketsPage from '@IssueConsolePages/TicketsPage';
import FormOverviewPage from '@IssueConsolePages/FormOverviewPage';
import PermissionsPage from '@IssueConsolePages/PermissionsPage';
import AgentsPage from '@IssueConsolePages/AgentsPage';
import FormReportsPage from '@IssueConsolePages/FormReportsPage';
import FieldsPage from '@IssueConsolePages/FieldsPage';
import NewFormWizard from '@IssueConsoleForms/NewFormWizard';
import FormCard from '@IssueConsoleRepresentationalDisplay/FormCard';
import LiveConnectionForm from '@IssueConsoleForms/LiveConnectionForm';
import ResolveReportExportForm from '@IssueConsoleForms/ResolveReportExportForm';
import DevelopmentConnectionForm from '@IssueConsoleForms/DevelopmentConnectionForm';
import { useReduxProject } from '@Hooks/reduxProjectHook/useReduxProject';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import { useReduxBillingUsage } from '@Hooks/reduxBillingUsageHook/useReduxBillingUsage';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import { useReduxOrg } from '@Hooks/reduxOrgHook/useReduxOrg';
import PaymentFailedBanner from '@CommonFunctionalDisplay/PaymentFailedBanner/PaymentFailedBanner';
import ProjectService from '@Helpers/URLHelpers/ProjectService';
import TicketService from '@Helpers/URLHelpers/KaiziqResolve/TicketService';
import KaiziqResolveConnectionService from '@Helpers/URLHelpers/KaiziqResolveConnectionService';
import UrlEncryptionService from '@Helpers/UrlEncryptionService';
import ResolveExportJobService from '@Helpers/URLHelpers/KaiziqResolve/ResolveExportJobService';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_ComStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './OverviewPage.scss';

const TAB_SLUGS = ['overview', 'responses', 'fields', 'reports', 'permissions', 'agents', 'settings'];

export default function OverviewPage(props) {

  const [kaiziqResolveConnectionList, setKaiziqResolveConnectionList] = useState([]);
  const [, setFormPosition] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [anchorPhoneTab, setAnchorPhoneTab] = useState(null);
  const [isPhoneTabOpen, setIsPhoneTabOpen] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [showReportExportForm, setShowReportExportForm] = useState(false);
  const [selectedList, setSelectedList] = useState([]);
  const [pageLoader, setPageLoader] = useState(true);
  const [tabPositionName, setTabPositionName] = useState('Overview');
  const [visibility, setVisibility] = useState('public');
  const [accessType, setAccessType] = useState(null);
  const [formTitle, setFormTitle] = useState(null);
  const [formTabPosition, setFormTabPosition] = useState(0);
  const [showCreateFormForm, setShowCreateFormForm] = useState(false);
  const [cursor, setCursor] = useState('wait');
  const [ticketStatusList, setTicketStatusList] = useState([]);
  const [createTimeStampList, setCreateTimeStampList] = useState([]);
  const [, setInternetException] = useState(false);
  const [showLiveConnectionForm, setShowLiveConnectionForm] = useState(false);
  const [showDevelopmentConnectionForm, setShowDevelopmentConnectionForm] = useState(false);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [autoOpenTicketId, setAutoOpenTicketId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const is0To992 = useMediaQuery({ maxWidth: 992 });

  const { currentProjectId, currentUserProjectPermissions } = useReduxProject();
  const { activeUserId } = useReduxActiveUser();
  const { usage, isOverLimit, isCreationBlocked } = useReduxBillingUsage();
  const { subscription } = useReduxSubscription();
  const { currentOrg } = useReduxOrg();
  const isOrgOwner = !currentOrg || currentOrg.ownerUserId === activeUserId;

  const ticketService = new TicketService();
  const resolveExportJobService = new ResolveExportJobService();
  const errorHandler = new ErrorHandler();
  new ProjectService();
  new UrlEncryptionService();
  const kaiziqResolveConnectionService = new KaiziqResolveConnectionService();

  const className = 'resolve__overviewPage';

  const encId = (id) => btoa(String(id));
  const decId = (str) => { try { return parseInt(atob(str), 10); } catch (_) { return NaN; } };

  useEffect(() => {
    renderData();
  }, []);

  const renderData = async () => {
    const listObj = await renderKaiziqResolveConnectionListData();
    setCursor('auto');
    const paramFormIndex = searchParams.get('formIndex');
    const deepLink = location.state?.deepLink;
    const paramFormId = searchParams.get('formId') || deepLink?.formId;
    const rawTicketParam = searchParams.get('ticketId') || deepLink?.ticketId;
    const paramTab = searchParams.get('tab');

    // Decode ticketId — supports both base64-encoded (new) and legacy raw numeric values
    let paramTicketId = null;
    if (rawTicketParam) {
      const decoded = decId(rawTicketParam);
      paramTicketId = isNaN(decoded) ? rawTicketParam : String(decoded);
    }

    const nextParams = new URLSearchParams(searchParams);
    if (currentProjectId) nextParams.set('ws', encId(currentProjectId));

    if (listObj?.length) {
      if (paramFormId) {
        const idx = listObj.findIndex(f => f.formId === paramFormId);
        if (idx !== -1) {
          // Use listObj[idx] directly — kaiziqResolveConnectionList state hasn't
          // re-rendered yet at this point so handleFormClick would read stale []
          const form = listObj[idx];
          setFormPosition(idx);
          setSelectedForm(form);
          try {
            let statusObj = await ticketService.getAnalysisByForm(form.id);
            statusObj = JSON.parse(statusObj.body);
            setTicketStatusList(statusObj.statusList);
            setCreateTimeStampList(statusObj.createTimeStampList);
          } catch (err) {
            errorHandler.handleError(err);
          }
          // Restore the tab — default to Responses (1) when coming from a deep link
          const tabIdx = paramTab ? TAB_SLUGS.indexOf(paramTab) : -1;
          const resolvedTab = tabIdx !== -1 ? tabIdx : 1;
          setFormTabPosition(resolvedTab);
          nextParams.set('tab', TAB_SLUGS[resolvedTab]);
          if (paramTicketId) {
            setAutoOpenTicketId(parseInt(paramTicketId, 10));
            nextParams.set('ticketId', encId(paramTicketId));
          }
        }
      } else if (paramFormIndex) {
        await handleFormClick('_', paramFormIndex);
        setPageLoader(false);
        return; // handleFormClick already updated URL
      }
    }

    setSearchParams(nextParams, { replace: true });
    setPageLoader(false);
  };

  const renderKaiziqResolveConnectionListData = async () => {
    let loadLoops = 0, loadingError = false, errorException, listObj;
    for (loadLoops = 0; loadLoops < 10; loadLoops++) {
      try {
        listObj = await kaiziqResolveConnectionService.getKaiziqResolveConnectionListByProject(currentProjectId);
        listObj = JSON.parse(listObj.body);
        setKaiziqResolveConnectionList(listObj);
        loadingError = false;
        break;
      } catch (err) {
        errorHandler.logError('Kaiziq Resolve Connection Data', err);
        loadingError = true;
        errorException = err;
      }
    }
    if (loadLoops === 10 && loadingError) {
      errorHandler.handleError(errorException);
      setInternetException(true);
      return false;
    }
    return listObj;
  };

  const timeout = (delay) => new Promise(res => setTimeout(res, delay));

  const handleFormClick = async (_, index) => {
    setFormPosition(index);
    const form = kaiziqResolveConnectionList[index];
    setSelectedForm(form);
    if (form) {
      const next = new URLSearchParams(searchParams);
      if (currentProjectId) next.set('ws', encId(currentProjectId));
      next.set('formId', form.formId);
      next.set('tab', 'overview');
      next.delete('ticketId');
      setSearchParams(next, { replace: true });
    }
    try {
      let statusObj = await ticketService.getAnalysisByForm(form.id);
      statusObj = JSON.parse(statusObj.body);
      setTicketStatusList(statusObj.statusList);
      setCreateTimeStampList(statusObj.createTimeStampList);
    } catch (err) {
      errorHandler.handleError(err);
    }
  };

  const closeOpenForm = () => {
    setFormPosition(null);
    setSelectedForm(null);
    const next = new URLSearchParams(searchParams);
    next.delete('formId');
    next.delete('tab');
    next.delete('ticketId');
    setSearchParams(next, { replace: true });
    // ws is intentionally kept
  };

  const handleFormTabPositionClick = (e, index) => {
    setFormTabPosition(index);
    setTabPositionName(e.target.innerText);
    closePhoneTab();
    const next = new URLSearchParams(searchParams);
    next.set('tab', TAB_SLUGS[index] || 'overview');
    next.delete('ticketId');
    setSearchParams(next, { replace: true });
  };

  const applyFilterForTicketsPage = (status) => {
    const ids = status.map(item => item.id);
    setSelectedList(ids);
    setFormTabPosition(1);
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'responses');
    setSearchParams(next, { replace: true });
  };

  const toggleCreateFormForm = () => setShowCreateFormForm(!showCreateFormForm);
  const toggleShowLiveConnectionForm = () => setShowLiveConnectionForm(!showLiveConnectionForm);
  const toggleShowDevelopmentConnectionForm = () => setShowDevelopmentConnectionForm(!showDevelopmentConnectionForm);
  const toggleShowTicketForm = () => setShowTicketForm(!showTicketForm);
  const handleRefreshClick = () => {
    setPageLoader(true);
    renderData();
  };

  const openPhoneTab = (event) => {
    setAnchorPhoneTab(event.currentTarget);
    setIsPhoneTabOpen(true);
  };
  const closePhoneTab = () => {
    setIsPhoneTabOpen(false);
    setAnchorPhoneTab(null);
  };

  const dataURLtoFile = (dataurl) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], 'file', { type: mime });
  };

  const Capitalize = (str) => str?.charAt(0)?.toUpperCase() + str?.slice(1);

  const handleDownloadMenu = (event) => {
    if (!showDownloadMenu) {
      if (event) {
        setDownloadMenuAnchor(event.currentTarget);
        setShowDownloadMenu(true);
      }
    } else {
      setDownloadMenuAnchor(null);
      setShowDownloadMenu(false);
    }
  };

  const toggleShowReportExportForm = () => {
    setShowReportExportForm(!showReportExportForm);
    if (showReportExportForm) handleDownloadMenu();
  };

  const handleDownloadReport = async () => {
    setDownloadingReport(true);
    setCursor('wait');
    try {
      let response = await resolveExportJobService.generateReportByFormId(selectedForm.id);
      if (response.status < 400) {
        response = JSON.parse(response.body);
        const filename = 'ResolveExport.pdf';
        const base64String = response.file;
        fileDownload(dataURLtoFile(base64String, filename), filename);
        handleDownloadMenu();
      }
    } catch (err) {
      errorHandler.handleError(err);
    }
    setDownloadingReport(false);
    setCursor('auto');
  };

  // ----------- UI RENDER HELPERS (pure JSX, no new logic) -----------

  const renderLoader = () => (
    <div className={className + '__loaderGrid'}>
      {[...Array(6)].map((_, i) => (
        <div key={i} className={className + '__loaderCard component-shadow'}>
          <div className={className + '__loaderCard__header'}>
            <Skeleton variant="text" width={120} height={28} />
            <Skeleton variant="text" width={60} height={28} />
          </div>
          <div className={className + '__loaderCard__body'}>
            <Skeleton variant="rectangular" width="100%" height={18} />
            <Skeleton variant="rectangular" width="80%" height={18} />
            <Skeleton variant="rectangular" width="60%" height={18} />
          </div>
          <div className={className + '__loaderCard__footer'}>
            <Skeleton variant="text" width={70} height={24} />
            <Skeleton variant="text" width={70} height={24} />
          </div>
        </div>
      ))}
    </div>
  );

  const renderEmptyState = () => (
    <div className={className + '__emptyState'}>
      <div className={className + '__emptyState__inner component-shadow'}>
        <div className={className + '__emptyState__title'}>No Forms Yet</div>
        <div className={className + '__emptyState__subtitle'}>
          Create your first form to start collecting and resolving tickets.
        </div>
        {currentUserProjectPermissions?.allowCreate && (
          <button
            className={className + '__emptyState__cta'}
            onClick={toggleCreateFormForm}
          >
            Get Started
          </button>
        )}
      </div>
    </div>
  );

  const renderFormsGrid = () => (
    <div className={className + '__formsSection'}>
      <div className={className + '__formsSection__header'}>
        <div className={className + '__formsSection__header__title'}>Forms</div>
        <div className={className + '__formsSection__header__actions'}>
          <div className="small-grey-text">Total: {kaiziqResolveConnectionList.length}</div>
          {currentUserProjectPermissions?.allowCreate && (
            <div
              className={className + '__formsSection__header__create click'}
              onClick={toggleCreateFormForm}
            >
              <AddCircleOutlineIcon />
              <span>New Form</span>
            </div>
          )}
        </div>
      </div>
      <Grid container spacing={2} className={className + '__formsSection__grid'}>
        {kaiziqResolveConnectionList.map((f, index) => (
          <Grid item xl={4} lg={6} md={12} sm={6} xs={12} key={f.id}>
            <FormCard
              kaiziqResolveConnection={f}
              handleFormClick={handleFormClick}
              index={index}
            />
          </Grid>
        ))}
        {currentUserProjectPermissions?.allowCreate && (
          <Grid item xl={4} lg={6} md={12} sm={6} xs={12}>
            <div
              className={className + '__formsSection__addCard component-shadow click'}
              onClick={toggleCreateFormForm}
            >
              <CreateIcon className="blue-text" />
              <div>Create Form</div>
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );

  const renderDetailHeader = () => (
    <div className={className + '__detail__header'}>

      {/* ── Left: icon + identity ── */}
      <div className={className + '__detail__header__left'}>
        <div className={className + '__detail__header__icon'}>
          {!!selectedForm?.photo ? (
            <img
              src={URL.createObjectURL(dataURLtoFile(selectedForm?.photo))}
              alt="form"
            />
          ) : (
            <DefaultFormPic />
          )}
        </div>
        <div className={className + '__detail__header__identity'}>
          <span className={className + '__detail__header__id'}>{selectedForm.formId}</span>
          <h2 className={className + '__detail__header__title'}>{selectedForm?.title}</h2>
          <div className={className + '__detail__header__chips'}>
            <span className={className + '__detail__header__chip'}>
              <ViewIcon /> {Capitalize(selectedForm?.visibility)}
            </span>
            <span className={className + '__detail__header__chip'}>
              <AccessIcon />
              {selectedForm?.accessType === 'public_open' && 'Open'}
              {(selectedForm?.accessType === 'public_verified' || selectedForm?.accessType === 'private_verified') && 'Verified'}
              {(selectedForm?.accessType === 'public_email' || selectedForm?.accessType === 'private_email') && 'Email Verified'}
            </span>
            <span className={
              className + '__detail__header__chip ' +
              className + '__detail__header__chip--live' +
              (selectedForm?.connectionLive ? ' is-live' : '')
            }>
              <span className={className + '__detail__header__chip__dot'} />
              {selectedForm?.connectionLive ? 'Live' : 'Offline'}
            </span>
          </div>
        </div>
      </div>

      {/* ── Right: close + actions + live toggle ── */}
      <div className={className + '__detail__header__right'}>
        <div className={className + '__detail__header__actions'}>
          {selectedForm.connectionLive && (
            <button
              className={className + '__detail__header__btn ' + className + '__detail__header__btn--primary'}
              onClick={toggleShowTicketForm}
              disabled={isCreationBlocked}
              title={isCreationBlocked ? 'Ticket creation is currently blocked due to billing limits' : undefined}
            >
              + Create Ticket
            </button>
          )}
          {selectedForm.connectionLive && (
            <button
              className={className + '__detail__header__btn ' + className + '__detail__header__btn--icon'}
              onClick={handleDownloadMenu}
              title="Download report"
            >
              <Download />
            </button>
          )}
          {showDownloadMenu && (
            <Menu
              anchorEl={downloadMenuAnchor}
              open={showDownloadMenu}
              onClose={handleDownloadMenu}
            >
              <MenuItem
                disabled={cursor === 'wait'}
                className={className + '__menuItem flex-row-center-1rem-gap'}
                onClick={handleDownloadReport}
              >
                <Download /> Download Report
              </MenuItem>
              <MenuItem
                className={className + '__menuItem flex-row-center-1rem-gap'}
                onClick={toggleShowReportExportForm}
              >
                <AvTimerRoundedIcon /> Schedule Report
              </MenuItem>
            </Menu>
          )}
          <button
            className={className + '__detail__header__btn ' + className + '__detail__header__btn--close'}
            onClick={closeOpenForm}
            title="Close"
          >
            <Close />
          </button>
        </div>

        <div className={className + '__detail__header__meta'}>
          <span className={className + '__detail__header__date'}>
            Created {moment(new Date(selectedForm?.createTimeStamp)).format('DD MMM, YYYY')}
          </span>
          <div className={className + '__detail__header__live-row'}>
            <span className={className + '__detail__header__live-label'}>Live</span>
            <ToggleStyleSwitch
              checked={selectedForm?.connectionLive}
              onChange={
                !selectedForm?.connectionLive
                  ? toggleShowLiveConnectionForm
                  : toggleShowDevelopmentConnectionForm
              }
              disabled={!currentUserProjectPermissions.allowAdmin}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderTabsNav = () => {
    const desktopTabs = (
      <Tabs
        value={formTabPosition}
        onChange={handleFormTabPositionClick}
        className={className + '__detail__tabs'}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab value={0} label="Overview" />
        <Tab value={1} label="Responses" />
        <Tab value={2} label="Fields" />
        <Tab value={3} label="Reports" />
        <Tab value={5} label="Agents" />
        {currentUserProjectPermissions.allowAdmin && (
          <Tab value={6} label="Settings" />
        )}
      </Tabs>
    );

    const mobileTabs = (
      <>
        <div
          className={className + '__detail__tabsMobile'}
          onClick={openPhoneTab}
        >
          {tabPositionName}
          <DropDownArrow />
        </div>
        {isPhoneTabOpen && (
          <Popover
            open={Boolean(anchorPhoneTab)}
            anchorEl={anchorPhoneTab}
            onClose={closePhoneTab}
          >
            <Tabs
              value={formTabPosition}
              onChange={handleFormTabPositionClick}
              orientation="vertical"
              className={className + '__detail__tabsPopover'}
              textColor="primary"
              indicatorColor="primary"
            >
              <Tab label="Overview" />
              <Tab label="Responses" />
              <Tab label="Fields" />
              <Tab label="Reports" />
              {currentUserProjectPermissions.allowAdmin && (
                <Tab
                  label={selectedForm.visibility === 'private' ? 'Permissions' : 'Reporters'}
                />
              )}
              <Tab label="Agents" />
              {currentUserProjectPermissions.allowAdmin && <Tab label="Settings" />}
            </Tabs>
          </Popover>
        )}
      </>
    );

    return (
      <div className={className + '__detail__nav component-shadow'}>
        {is0To992 ? mobileTabs : desktopTabs}
      </div>
    );
  };

  const renderDetailContent = () => (
    <div className={className + '__detail__content component-shadow'}>
      {formTabPosition === 0 && (
        <FormOverviewPage
          kaiziqResolveConnection={selectedForm}
          handleFormTabPositionClick={handleFormTabPositionClick}
          createTimeStampList={createTimeStampList}
          ticketStatusList={ticketStatusList}
          applyFilterForTicketsPage={applyFilterForTicketsPage}
          closeOpenForm={closeOpenForm}
        />
      )}
      {formTabPosition === 1 && (
        <TicketsPage
          kaiziqResolveConnection={selectedForm}
          selectedList={selectedList}
          autoOpenTicketId={autoOpenTicketId}
          onAutoOpenComplete={() => {
            setAutoOpenTicketId(null);
            const next = new URLSearchParams(searchParams);
            next.delete('ticketId');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
      {formTabPosition === 2 && (
        <FieldsPage kaiziqResolveConnection={selectedForm} />
      )}
      {formTabPosition === 3 && (
        <FormReportsPage
          kaiziqResolveConnection={selectedForm}
          reloadData={renderData}
        />
      )}
      {formTabPosition === 4 && (
        <PermissionsPage kaiziqResolveConnection={selectedForm} />
      )}
      {formTabPosition === 5 && (
        <AgentsPage
            kaiziqResolveConnection={selectedForm}
            reloadData={renderData}
        />
      )}
      {formTabPosition === 6 && (
        <SettingsPage
          kaiziqResolveConnection={selectedForm}
          reloadData={renderData}
        />
      )}
    </div>
  );

  return (
    <div className={className} style={{ cursor }}>
      <PaymentFailedBanner subscription={subscription} isOwner={isOrgOwner} />
      {showCreateFormForm && (
        <NewFormWizard
          onClose={toggleCreateFormForm}
          onComplete={handleRefreshClick}
        />
      )}
      {!showCreateFormForm && <div className={className + '__inner'}>
        <div className={className + '__layout'}>
          {pageLoader
            ? renderLoader()
            : (
              selectedForm
                ? (
                  <div className={className + '__detail'}>
                    {renderDetailHeader()}
                    {isOverLimit && (
                      <div className={`${className}__detail__overageBanner${isCreationBlocked ? ` ${className}__detail__overageBanner--blocked` : ''}`}>
                        <span className={className + '__detail__overageBanner__icon'}>
                          {isCreationBlocked ? '🚫' : '⚠'}
                        </span>
                        <span>
                          {isCreationBlocked
                            ? usage?.ticketLimitMode === 'hard'
                              ? <>Ticket creation is <strong>blocked</strong>. You've reached your hard limit of <strong>{usage?.planLimitSnapshot} tickets</strong> this month.</>
                              : <>Ticket creation is <strong>blocked</strong>. You have unpaid overage from last month. Please clear your balance to create more tickets.</>
                            : <>You've reached your <strong>{usage?.planLimitSnapshot}-ticket</strong> monthly limit. Additional tickets will be counted as <strong>overage</strong> and billed accordingly.</>
                          }
                        </span>
                      </div>
                    )}
                    {renderTabsNav()}
                    {renderDetailContent()}
                  </div>
                )
                : (
                  kaiziqResolveConnectionList.length === 0
                    ? (
                      <>
                        {renderEmptyState()}
                      </>
                    )
                    : (
                      <>
                        {renderFormsGrid()}
                      </>
                    )
                )
            )
          }
        </div>
      </div>}

      {showTicketForm && (
        <TicketForm
          open={showTicketForm}
            kaiziqResolveConnection={selectedForm}
          onClose={toggleShowTicketForm}
        />
      )}
      {showLiveConnectionForm && (
        <LiveConnectionForm
          open={showLiveConnectionForm}
          kaiziqResolveConnection={selectedForm}
          closePopup={toggleShowLiveConnectionForm}
          reloadData={renderData}
        />
      )}
      {showDevelopmentConnectionForm && (
        <DevelopmentConnectionForm
          open={showDevelopmentConnectionForm}
          kaiziqResolveConnection={selectedForm}
          closePopup={toggleShowDevelopmentConnectionForm}
          reloadData={renderData}
        />
      )}
      {showReportExportForm && (
        <ResolveReportExportForm
          open={showReportExportForm}
          closePopup={toggleShowReportExportForm}
          kaiziqResolveConnection={selectedForm}
        />
      )}
      {downloadingReport && (
        <div className={className + '__downloadOverlay'}>
          <div className={className + '__downloadOverlay__content'}>
            <CircularProgress size={60} />
            <div className={className + '__downloadOverlay__text'}>
              Generating Report...
            </div>
            <div className={className + '__downloadOverlay__subtext'}>
              Please wait while we prepare your download
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
